// services/userProfile.services.js
import axios from 'axios';
import { createHeaders } from './headerUtils';
import logger from '../utils/logger';

const BASE_URL = process.env.REACT_APP_BASE_URL;

/**
 * Service class for interacting with user profile APIs
 */
// Used to deduplicate identical API requests in quick succession
let pendingRequests = {};

const UserProfileServices = {

  getCurrentUser: async () => {
    try {
      const apiUrl = `${BASE_URL}/profile/me`;
      if (pendingRequests[apiUrl]) {
        logger.debug('Returning cached request for /profile/me to avoid duplicate calls');
        return pendingRequests[apiUrl];
      }
      
      const headers = createHeaders();
      pendingRequests[apiUrl] = new Promise(async (resolve, reject) => {
        try {
          const response = await axios.get(apiUrl, { headers });
          setTimeout(() => {
            delete pendingRequests[apiUrl];
          }, 100);
          resolve(response.data);
        } catch (error) {
          delete pendingRequests[apiUrl];
          logger.error('Error fetching current user profile:', error);
          reject(error);
        }
      });
      
      return pendingRequests[apiUrl];
    } catch (error) {
      logger.error('Error fetching current user profile:', error);
      throw error;
    }
  },
  /**
   * Update the current user's profile
   * @param {Object} profileData - Profile data to update
   */
  updateUserProfile: async (profileData) => {
    try {
      const validatedData = UserProfileServices.validateProfileFields(profileData);
      const apiUrl = `${BASE_URL}/profile/update-me`;
      const headers = createHeaders();
      const response = await axios.put(apiUrl, validatedData, { headers });
      return response;
    } catch (error) {
      // Handle validation errors from backend (400 status with success: false)
      if (error.response && error.response.status === 400 && error.response.data) {
        // Return the error response data so the frontend can handle validation messages
        return error.response;
      }
      
      logger.error('Error updating user profile:', error);
      throw error;
    }
  },
  
  /**
   * Generate a URL for uploading a profile picture
   * @param {string} fileType - MIME type of the file
   */
  getProfilePictureUploadUrl: async (fileType) => {
    try {
      const apiUrl = `${BASE_URL}/profile/me/profile-picture`;
      const headers = createHeaders();
      const response = await axios.post(apiUrl, { fileType }, { headers });
      return response.data;
    } catch (error) {
      logger.error('Error generating profile picture upload URL:', error);
      throw error;
    }
  },
  
  /**
   * Directly upload profile picture using Multer
   * @param {File} file - The image file to upload
   * @param {boolean} setAsActive - Whether to set the uploaded photo as active (default: false for PhotoGallery)
   * @returns {Promise<Object>} - The response containing the new profile picture URL
   */
  uploadProfilePicture: async (file, setAsActive = false) => {
    try {
      const apiUrl = `${BASE_URL}/profile/me/profile-picture/upload${setAsActive ? '?setAsActive=true' : ''}`;
      const headers = createHeaders();
      const formData = new FormData();
      formData.append('profileImage', file);
      
      const response = await axios.post(apiUrl, formData, { 
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return response;
    } catch (error) {
      logger.error('Error uploading profile picture:', error);
      throw error;
    }
  },
  
  /**
   * Remove the user's profile picture
   */
  removeProfilePicture: async () => {
    try {
      const apiUrl = `${BASE_URL}/profile/me/profile-picture`;
      const headers = createHeaders();
      const response = await axios.delete(apiUrl, { headers });
      return response;
    } catch (error) {
      logger.error('Error removing profile picture:', error);
      throw error;
    }
  },
  
  /**
   * Validate and filter allowed profile fields
   * @param {Object} profileData - The profile data to validate
   * @returns {Object} - The validated profile data
   */
  validateProfileFields: (profileData) => {
    // Define allowed fields and their validation rules
    const allowedFields = {
      preferredName: {
        validate: (value) => typeof value === 'string' && value.length <= 50,
        errorMsg: 'Preferred name must be a string with maximum 50 characters'
      },
      birthday: {
        validate: (value) => {
          if (!value) return true; // Allow empty values
          const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
          return dateRegex.test(value);
        },
        errorMsg: 'Birthday must be in YYYY-MM-DD format'
      },
      statusMessage: {
        validate: (value) => typeof value === 'string' && value.length <= 200,
        errorMsg: 'Status message must be a string with maximum 200 characters'
      },
      timezone: {
        validate: (value) => {
          if (!value) return true;
          return typeof value === 'string';
        },
        errorMsg: 'Invalid timezone format'
      },
      city: {
        validate: (value) => typeof value === 'string' && value.length <= 100,
        errorMsg: 'City must be a string with maximum 100 characters'
      }
    };
    
    const invalidFields = Object.keys(profileData).filter(key => 
      !allowedFields[key] || !allowedFields[key].validate(profileData[key])
    );
    
    if (invalidFields.length > 0) {
      const errors = invalidFields.map(field => {
        return allowedFields[field] 
          ? allowedFields[field].errorMsg 
          : `Field '${field}' is not allowed for updates`;
      });
      
      throw new Error(`Profile validation failed: ${errors.join(', ')}`);
    }
    
    // Return only allowed fields
    const validatedData = {};
    Object.keys(profileData).forEach(key => {
      if (allowedFields[key]) {
        validatedData[key] = profileData[key];
      }
    });
    
    return validatedData;
  },

  /**
   * Get profile configuration settings
   */
  getProfileSettings: async () => {
    try {
      const apiUrl = `${BASE_URL}/profile/settings`;
      const response = await axios.get(apiUrl);
      return response.data;
    } catch (error) {
      logger.error('Error fetching profile settings:', error);
      throw error;
    }
  },

  /**
   * Get all photos for the current user
   */
  getUserPhotos: async () => {
    try {
      const apiUrl = `${BASE_URL}/profile/me/photos`;
      const headers = createHeaders();
      const response = await axios.get(apiUrl, { headers });
      return response.data;
    } catch (error) {
      logger.error('Error fetching user photos:', error);
      throw error;
    }
  },

  /**
   * Set a specific photo as the active profile picture
   * @param {string} photoKey - The S3 key of the photo to set as active
   */
  setActivePhoto: async (photoKey) => {
    try {
      const apiUrl = `${BASE_URL}/profile/me/photos/active`;
      const headers = createHeaders();
      const response = await axios.put(apiUrl, { photoKey }, { headers });
      return response.data;
    } catch (error) {
      logger.error('Error setting active photo:', error);
      throw error;
    }
  },

  /**
   * Unset the active profile picture (keep photo in collection but remove active status)
   */
  unsetActivePhoto: async () => {
    try {
      const apiUrl = `${BASE_URL}/profile/me/photos/active`;
      const headers = createHeaders();
      const response = await axios.delete(apiUrl, { headers });
      return response.data;
    } catch (error) {
      logger.error('Error unsetting active photo:', error);
      throw error;
    }
  },

  /**
   * Delete a specific photo from user's collection
   * @param {string} photoKey - The S3 key of the photo to delete
   * @param {boolean} skipAutoReplace - If true, don't auto-replace deleted active photo (for EditUserProfile)
   */
  deleteSpecificPhoto: async (photoKey, skipAutoReplace = false) => {
    try {
      const apiUrl = `${BASE_URL}/profile/me/photos/${encodeURIComponent(photoKey)}`;
      const headers = createHeaders();
      
      // Add query parameter if skipAutoReplace is true
      const params = skipAutoReplace ? { skipAutoReplace: 'true' } : {};
      
      const response = await axios.delete(apiUrl, { 
        headers,
        params
      });
      return response.data;
    } catch (error) {
      logger.error('Error deleting photo:', error);
      throw error;
    }
  }
};

export default UserProfileServices;
